import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { warmGoogleIdentity } from '../../lib/googleIdentity';
import { getPendingSave } from '../../utils/pendingSave';
import { getPostAuthRedirect } from '../../utils/postAuthRedirect';
import styles from '../../pages/UserLoginPage.module.css';

interface LoginFormProps {
  /**
   * If true, the form is rendered inside the AuthModal shell.
   * In modal mode the inner form skips its own outer .page/.card chrome —
   * the modal supplies the surface.
   */
  embedded?: boolean;
  /**
   * Called after a successful login when the post-auth destination matches
   * the current background page (so the parent can simply dismiss the modal
   * instead of triggering a route change). Returns true if handled.
   */
  onLoginSuccessSamePage?: (destination: string) => boolean;
}

/**
 * LoginForm — the actual login form markup + behavior, extracted from
 * UserLoginPage so that both the standalone page and AuthModal can render it.
 *
 * The auth flow itself (Supabase email/password + Google OAuth) is unchanged.
 * Post-auth redirect logic (getPostAuthRedirect) is identical to the previous
 * standalone page. The only addition is an optional `onLoginSuccessSamePage`
 * callback used by AuthModal to dismiss the modal without re-navigating when
 * the destination matches the background page.
 */
export function LoginForm({ embedded = false, onLoginSuccessSamePage }: LoginFormProps) {
  const { user, isLoading, signInWithGoogle, signInWithEmail } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Preserve backgroundLocation across cross-auth links (login -> signup -> reset)
  // so the user can swap modal contents without losing the underlying page.
  const passThroughState = useMemo(() => {
    const s = location.state as { backgroundLocation?: Location } | null;
    return s?.backgroundLocation ? { backgroundLocation: s.backgroundLocation } : undefined;
  }, [location.state]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [hasPendingSave, setHasPendingSave] = useState(false);

  useEffect(() => {
    setHasPendingSave(getPendingSave() !== null);
  }, []);

  // T205-FE: GIS is no longer in index.html. The form being on screen is the signal that
  // a Google click may follow, so start the download now; the click still awaits the loader.
  useEffect(() => {
    if (import.meta.env.VITE_GOOGLE_CLIENT_ID) warmGoogleIdentity();
  }, []);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!email.trim() || !password.trim()) {
        setError('Inserisci email e password');
        return;
      }

      setSubmitting(true);
      try {
        await signInWithEmail(email, password);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore di autenticazione');
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, signInWithEmail]
  );

  const handleGoogleClick = useCallback(async () => {
    setError(null);
    setGoogleSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore con Google');
      setGoogleSubmitting(false);
    }
  }, [signInWithGoogle]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void getPostAuthRedirect(user.id).then((dest) => {
      if (cancelled) return;
      // Modal mode: if destination matches the page underneath the modal,
      // let parent dismiss the modal instead of triggering a navigation.
      if (onLoginSuccessSamePage && onLoginSuccessSamePage(dest)) return;
      navigate(dest, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [user, navigate, onLoginSuccessSamePage]);

  if (isLoading) return null;

  const inner = (
    <>
      <div className={styles.header}>
        <p className={styles.subtitle}>Accedi al tuo account</p>
      </div>

      {hasPendingSave && (
        <p className={styles.pendingBanner} role="status">
          Accedi per salvare l&apos;evento selezionato
        </p>
      )}

      <button
        type="button"
        className={styles.googleButton}
        onClick={handleGoogleClick}
        disabled={googleSubmitting || submitting}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
          />
          <path
            fill="#FBBC05"
            d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
          />
        </svg>
        {googleSubmitting ? 'Apertura Google...' : 'Continua con Google'}
      </button>

      <div className={styles.divider}>
        <span>oppure</span>
      </div>

      <form className={styles.form} onSubmit={handleEmailSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="user-login-email" className={styles.label}>
            Email
          </label>
          <input
            id="user-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@esempio.it"
            className={styles.input}
            autoComplete="email"
            disabled={submitting}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="user-login-password" className={styles.label}>
            Password
          </label>
          <input
            id="user-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="La tua password"
            className={styles.input}
            autoComplete="current-password"
            disabled={submitting}
          />
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <button type="submit" className={styles.submitButton} disabled={submitting}>
          {submitting ? (
            <>
              <span className={styles.spinner} />
              Accesso in corso...
            </>
          ) : (
            'Accedi'
          )}
        </button>
      </form>

      <div className={styles.links}>
        <Link to="/password-reset" state={passThroughState} className={styles.link}>
          Password dimenticata?
        </Link>
        <span className={styles.signup}>
          Non hai un account?{' '}
          <Link to="/registrati" state={passThroughState} className={styles.link}>
            Registrati
          </Link>
        </span>
      </div>
    </>
  );

  // Embedded: skip outer .page/.card chrome — caller (AuthModal) provides it.
  if (embedded) return inner;

  return <div className={styles.card}>{inner}</div>;
}
